import { Entity } from '@adornis/baseql/decorators.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { parseBoolean, parseDate, parseDateToBool } from '@adornis/import-export/db/default-parse-functions.js';
import { serializeDate } from '@adornis/import-export/db/default-serialize-functions.js';
import { Mitarbeiter, Vertragsarten } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Adresse } from '../adresse.js';
import { Gender } from '../enums/enums.js';

@Entity()
export class EmployeeConvertConfig extends ConvertConfig<Mitarbeiter> {
  public static override _class = 'EmployeeConvertConfig';

  constructor() {
    super({
      delimiter: ';',
      className: Mitarbeiter._class,
      uniqueIdentifierFieldName: 'employeeID',
      fieldMappings: [
        new FieldMappingEntry({ output: 'employeeID', input: 'ID' }, v => (v ? +v : 0)),
        new FieldMappingEntry({ output: 'vorname', input: null }, (v, data) => {
          v = data.__unused_Name;
          return v?.split(',')[1]?.trim();
        }),
        new FieldMappingEntry({ output: 'nachname', input: null }, (v, data) => {
          v = data.__unused_Name;
          return v?.split(',')[0]?.trim();
        }),
        new FieldMappingEntry(
          { output: null, input: 'Vorname' },
          undefined,
          (v, data, i) => (data as Mitarbeiter).vorname!,
        ),
        new FieldMappingEntry(
          { output: null, input: 'Nachname' },
          undefined,
          (v, data, i) => (data as Mitarbeiter).nachname!,
        ),
        new FieldMappingEntry({ output: 'geschlecht', input: 'Person_Geschlecht' }, (v, data) => {
          switch (v) {
            case 'weiblich':
              return Gender.FEMALE;
            case 'männlich':
              return Gender.MALE;
            default:
              return Gender.DIVERSE;
          }
        }),
        new FieldMappingEntry({ output: 'geburtstag', input: 'Person_Geb' }, parseDate, serializeDate()),
        new FieldMappingEntry(
          { output: 'adresse', input: 'STR' },
          (v, data) => {
            return new Adresse({
              strasse: v,
              zip: data.__unused_PLZ,
              ort: data.__unused_Stadt,
            });
          },
          (v, data, i) => {
            const employee = data as Mitarbeiter;
            return employee.adresse?.strasse ?? '';
          },
        ),
        new FieldMappingEntry({ output: null, input: 'PLZ' }, undefined, (v, data, i) => {
          const employee = data as Mitarbeiter;
          return employee.adresse?.zipCity?.zip ?? '';
        }),
        new FieldMappingEntry({ output: null, input: 'Ort' }, undefined, (v, data, i) => {
          const employee = data as Mitarbeiter;
          return employee.adresse?.zipCity?.city ?? '';
        }),
        new FieldMappingEntry({ output: 'email', input: 'Person_Mail' }),
        new FieldMappingEntry({ output: 'emailChancenwerk', input: 'Mail_CW' }),
        new FieldMappingEntry({ output: 'handynummer', input: 'Person_Handy' }),
        new FieldMappingEntry({ output: 'festnetz', input: 'Person_Telefon' }),
        new FieldMappingEntry({ output: 'hochschule', input: 'Hochschule' }),
        new FieldMappingEntry({ output: 'studiengang', input: 'Studiengang' }),
        new FieldMappingEntry({ output: 'staatsangehoerigkeit', input: 'Staatsangehoerigkeit' }),
        new FieldMappingEntry(
          { output: 'kontaktaufnahmeEmail', input: 'Kontaktaufnahme_ueber_email' },
          parseBoolean(false),
        ),
        new FieldMappingEntry(
          { output: 'kontaktaufnahmeHandynummer', input: 'Kontaktaufnahme_ueber_mobilnummer' },
          parseBoolean(false),
        ),
        new FieldMappingEntry(
          { output: 'kontaktaufnahmeFestnetznummer', input: 'Kontaktaufnahme_ueber_festnetznummer' },
          parseBoolean(false),
        ),
        new FieldMappingEntry({ output: 'kontoInformationen.kontoinhaber', input: 'Kontoinhaber' }),
        new FieldMappingEntry({ output: 'kontoInformationen.iban', input: 'IBAN' }),
        new FieldMappingEntry({ output: 'kontoInformationen.bic', input: 'BIC' }),
        new FieldMappingEntry({ output: 'kontoInformationen.kreditinstitut', input: 'Kreditinstitut' }),
        new FieldMappingEntry(
          { output: 'vertraege.0.startDatum', input: 'Beginn_Mitgliedschaft' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'vertraege.0.taetigkeiten.0.endDatum', input: 'Ende_Mitgliedschaft' },
          parseDate,
        ),
        new FieldMappingEntry({ output: 'vertraege.0.vertragsart', input: 'Beschaeftigungsyp' }, v => {
          switch (v) {
            case 'Übungsleiter':
            case 'Übungsleiter (Tätigkeitsabrg.)':
              return Vertragsarten.UEBUNGSLEITER;
            case 'Ehrenamtler (keine Abrg.)':
              return Vertragsarten.EHRENAMTLER;
            case 'unbekannt':
              return null;
            case 'Werkstudent':
              return Vertragsarten.WERKSTUDENT;
            case 'Praktikum':
              return Vertragsarten.PRAKTIKUM;
            case 'Minijob':
              return Vertragsarten.MINIJOB;
            case 'Anstellung':
              return Vertragsarten.ANSTELLUNG;
            default:
              throw new Error(`unsupported vertragsart (${v})`);
          }
        }),
        new FieldMappingEntry(
          { output: 'unterlagen.datenschutzerklaerungErhalten', input: 'Datenschutzerklaerung_erhalten' },
          parseDateToBool,
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.ersthelferkursAbsolviertAm', input: 'ErsthelferkursAbsolviertAm' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'unterlagen.aktuelleMasernimpfungNachgewiesen',
            input: 'Masernimpfung_EinwilligungErteiltAm',
          },
          parseDateToBool,
        ),
        new FieldMappingEntry(
          {
            output: 'unterlagen.selbstverplichtungserklaerungErhalten',
            input: 'Selbstverpflichtungserklaerung_erhalten',
          },
          (v, data, i) =>
            parseDateToBool(v) || parseDateToBool(data.__unused_Selbstverpflichtungserklaerung_digital_erhalten),
        ),
        new FieldMappingEntry(
          {
            output: 'unterlagen.vereinbarungUrheberrechtErhalten',
            input: 'VereinbarungZumUrheberrechtErhaltenAm',
          },
          parseDateToBool,
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.einwilligungserklaerungMasernimpfungNachweisErhalten', input: 'Masernimpfung_am' },
          parseDateToBool,
        ),
        new FieldMappingEntry({ output: 'unterlagen.personalNummer', input: 'Personalnummer' }),
        new FieldMappingEntry({ output: 'unterlagen.kreditorenNummer', input: 'Kreditorennummer' }),
        new FieldMappingEntry(
          { output: 'unterlagen.vertragVersendet', input: 'Vertrag_versendet' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.vertragErhalten', input: 'Vertrag_erhalten' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.zusatzvereinbarungVersendet', input: 'Zusatzvereinbarung_versendet' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.zusatzvereinbarungErhalten', input: 'Zusatzvereinbarung_erhalten' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.fuehrungzeugnisGueltigAb', input: 'Fuehrungszeugnis_erhalten' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'unterlagen.fuehrungzeugnisGueltigBis',
            input: 'Fuehrungszeugnis_gueltig_bis',
          },

          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'unterlagen.byodFormularErhalten', input: 'BYOD_FormularErhaltenAm' },
          parseDateToBool,
        ),
        new FieldMappingEntry(
          { output: 'onboarding.einarbeitungAbsolviertAm', input: 'CHANCENspirit_(CHANCENakademie)_absolviert_am' },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'onboarding.onboardingAkademieAbsolviertAm',
            input: 'Einführung_der_Lernkaskade_(CHANCENakademie)_absolviert_am',
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedOSWorkshopOneRoleModel',
            input: `An Schulungen des OS-Workshops 1 \"Vorbild sein\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.carriedOutOSWorkshopOneRoleModel',
            input: `OS-Workshop 1 \"Vorbild sein\" durchgeführt am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedOSWorkshopTwoChallenges',
            input: `An Schulungen des OS-Workshops 2 \"Umgang mit Herausforderungen\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.carriedOutOSWorkshopTwoChallenges',
            input: `OS-Workshop 2 \"Umgang mit Herausforderungen\" durchgeführt am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedSuSWorkshopLearningToLearn',
            input: `An Schulungen des Sus-Workshops \"Lernen lernen\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.carriedOutSuSWorkshopLearningToLearn',
            input: `Sus-Workshop \"Lernen lernen\" durchgeführt am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedSuSWorkshopMentalStrength',
            input: `An Schulungen des Sus-Workshops \"Mentale Stärke\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.carriedOutSuSWorkshopMentalStrength',
            input: `SuS-Workshop \"Mentale Stärke\" durchgeführt am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedSusWorkshopInnerStrenght',
            input: `An Schulung des SuS-Workshops \"Deine innere Kraft\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.carriedOutSusWorkshopInnerStrenght',
            input: `SuS-Workshop \"Deine innere Kraft\" durchgeführt am`,
          },
          parseDate,
          serializeDate(),
        ),

        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULTrainingOSCompanion',
            input: `An Schulung \"Einführung OS-Begleitung & Akquise\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopCompentenceOne',
            input: `An Workshop \"Interkulturelle Kompetenzen - Teil 1\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopCompentenceTwo',
            input: `An Workshop \"Interkulturelle Kompetenzen - Teil 2\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopMotivation',
            input: `An Workshop \"Motivationsworkshop...gar kein Bock!\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopToDos',
            input: `An Workshop \"Ich & meine ToDo's\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopStress',
            input: `An Workshop \"Wenn der Stress dich stresst\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopCommunication',
            input: `An Workshop \"Kann man nicht nicht kommunizieren?\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopStorytelling',
            input: `An Workshop \"Identifikation & Motivation durch Storytelling\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopLanguage',
            input: `An Workshop \"Sprache & Sprachsensibilität im Fachunterricht\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'workshopsAndTrainingInformation.participatedULWorkshopDidacticReduction',
            input: `An Workshop \"Diaktische Reduktion\" teilgenommen am`,
          },
          parseDate,
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'unterlagen.kommunikationMitUL', input: 'Kommunikation_mit_ÜL' }),
        new FieldMappingEntry({ output: 'unterlagen.sonstigeVermerke', input: 'Sonstige_Vermerke' }),
      ],
      foreignKeys: [],
    });
  }
}
